// Shared page SEO helpers: title, description, canonical, Open Graph, Twitter, JSON-LD.

export const SITE_ORIGIN = "https://www.advmeas.com";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;

// Upsert a meta tag by name= or property=.
function upsertMeta(attr, key, content) {
   if (!content) return;
   let el = document.querySelector(`meta[${attr}="${key}"]`);
   if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
   }
   el.setAttribute("content", content);
}

// Upsert a <link rel="..."> tag (canonical, etc.).
function upsertLink(rel, href) {
   if (!href) return;
   let el = document.querySelector(`link[rel="${rel}"]`);
   if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", rel);
      document.head.appendChild(el);
   }
   el.setAttribute("href", href);
}

// Upsert a JSON-LD script by id so SPA navigations can replace it.
function upsertJsonLd(id, data) {
   const scriptId = id || "ami-jsonld";
   let el = document.getElementById(scriptId);
   if (!data) {
      if (el) el.remove();
      return;
   }
   if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = scriptId;
      document.head.appendChild(el);
   }
   el.textContent = JSON.stringify(data);
}

// Absolute page URL from a path like "/" or "/mpc".
export function pageUrl(path) {
   if (!path || path === "/") return `${SITE_ORIGIN}/`;
   const normalized = path.startsWith("/") ? path : `/${path}`;
   return `${SITE_ORIGIN}${normalized}`;
}

// Organization schema for the company (home / about).
export function organizationJsonLd() {
   return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Advanced Measurements",
      alternateName: "AMI",
      url: SITE_ORIGIN,
      logo: `${SITE_ORIGIN}/ami-logo.png`,
      sameAs: [
         "https://www.linkedin.com/company/ami-controls-analytics",
      ],
      address: {
         "@type": "PostalAddress",
         addressLocality: "Calgary",
         addressRegion: "AB",
         addressCountry: "CA",
      },
   };
}

// Product / software schema for product pages.
export function productJsonLd({ name, description, path, image }) {
   return {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name,
      description,
      url: pageUrl(path),
      image: image || DEFAULT_OG_IMAGE,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Cross-platform",
      provider: {
         "@type": "Organization",
         name: "Advanced Measurements",
         url: SITE_ORIGIN,
      },
   };
}

// Service schema for support / field help pages.
export function serviceJsonLd({ name, description, path }) {
   return {
      "@context": "https://schema.org",
      "@type": "Service",
      name,
      description,
      url: pageUrl(path),
      provider: {
         "@type": "Organization",
         name: "Advanced Measurements",
         url: SITE_ORIGIN,
      },
      areaServed: "Worldwide",
      serviceType: "Industrial field support",
   };
}

// BreadcrumbList from [{ name, path }, ...].
export function breadcrumbJsonLd(items) {
   return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, index) => ({
         "@type": "ListItem",
         position: index + 1,
         name: item.name,
         item: pageUrl(item.path),
      })),
   };
}

/**
 * Apply document title, description, canonical, Open Graph, Twitter, optional JSON-LD.
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} [opts.keywords]
 * @param {string} [opts.path] - route path for canonical/og:url
 * @param {string} [opts.image] - absolute image URL for OG/Twitter
 * @param {string} [opts.type] - og:type (default website)
 * @param {string} [opts.robots] - robots meta (e.g. "noindex, follow")
 * @param {object|object[]|null} [opts.jsonLd] - schema.org graph, object, or null to clear
 */
export function setPageSeo({
   title,
   description,
   keywords,
   path,
   image,
   type,
   robots,
   jsonLd,
}) {
   const url = path != null ? pageUrl(path) : undefined;
   const ogImage = image || DEFAULT_OG_IMAGE;

   if (title) document.title = title;
   if (description) upsertMeta("name", "description", description);
   if (keywords) upsertMeta("name", "keywords", keywords);
   if (robots) upsertMeta("name", "robots", robots);
   else {
      // Clear noindex when leaving a NotFound page.
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.remove();
   }

   if (url) upsertLink("canonical", url);

   // Open Graph
   upsertMeta("property", "og:site_name", "Advanced Measurements");
   upsertMeta("property", "og:locale", "en_CA");
   upsertMeta("property", "og:type", type || "website");
   if (title) upsertMeta("property", "og:title", title);
   if (description) upsertMeta("property", "og:description", description);
   if (url) upsertMeta("property", "og:url", url);
   upsertMeta("property", "og:image", ogImage);

   // Twitter summary card
   upsertMeta("name", "twitter:card", "summary_large_image");
   if (title) upsertMeta("name", "twitter:title", title);
   if (description) upsertMeta("name", "twitter:description", description);
   upsertMeta("name", "twitter:image", ogImage);

   // JSON-LD: single object, @graph array, or null to clear
   if (jsonLd) {
      const payload = Array.isArray(jsonLd)
         ? { "@context": "https://schema.org", "@graph": jsonLd }
         : jsonLd;
      upsertJsonLd("ami-jsonld", payload);
   } else if (jsonLd === null) {
      upsertJsonLd("ami-jsonld", null);
   }
}
